<script context="module">
  // XXX this is only needed, because Design System CSS overwrites color for <strong> elements
  const CustomBold = Bold.extend({
    renderHTML({ HTMLAttributes }) {
      return ['b', HTMLAttributes, 0]
    }
  })

  // This used to take any currently set font-size on paragraphs and
  // initialize it with the textStyle fontSize attribute.
  const ParagraphMigration = Paragraph.extend({
    addProseMirrorPlugins() {
      let latestMarks = []
      let saveNextTime = true

      return [
        new Plugin({
          appendTransaction(txs, oldState, newState) {
            if (newState.doc.textContent.length === 0 && saveNextTime) {
              latestMarks = oldState.selection.$from.marks()
              saveNextTime = false
            } else if (newState.doc.textContent.length > 0 && !saveNextTime) {
              saveNextTime = true

              let newTr = newState.tr
              for (const mark of latestMarks) newTr = newTr.addMark(0, newState.doc.content.size, mark)
              return newTr
            }
          }
        })
      ]
    },
    addAttributes() {
      return {
        fontSize: {
          parseHTML: element => element.style.fontSize.replace(/[^0-9\.]/g, '') || null,
          renderHTML: attributes => ({})
        }
      }
    }
  })
</script>

<script>
  import { onMount, onDestroy, createEventDispatcher, setContext } from 'svelte'
  import { Editor, generateHTML } from '@tiptap/core'
  import { Color } from '@tiptap/extension-color'
  import TextStyle from '@tiptap/extension-text-style'
  import FontFamily from '@tiptap/extension-font-family'
  import Underline from '@tiptap/extension-underline'
  import Italic from '@tiptap/extension-italic'
  import Bold from '@tiptap/extension-bold'
  import Code from '@tiptap/extension-code'
  import Document from '@tiptap/extension-document'
  import Paragraph from '@tiptap/extension-paragraph'
  import Text from '@tiptap/extension-text'
  import Strike from '@tiptap/extension-strike'
  import TextAlign from '@tiptap/extension-text-align'
  import Heading from '@tiptap/extension-heading'
  // import Highlight from '@tiptap/extension-highlight'
  import { Plugin } from '@tiptap/pm/state'
  import { Fragment, Slice } from '@tiptap/pm/model'
  import { faAlignCenter, faAlignJustify, faAlignLeft, faAlignRight } from '@fortawesome/pro-regular-svg-icons'
  import WysiwygDropdown from './WysiwygDropdown.svelte'
  import FontSizeExtension from './FontSizeExtension.js'
  import { cssPrefix } from '../helper-classes.js'
  import { rgbStringToHex } from '../ChromaPicker/helpers.js'
  import { ColorPicker } from '../FormElements'
  import * as FormTheme from '../FormTheme'
  import FillEditor from './FillEditor.svelte'
  setContext('FormTheme', FormTheme)

  const dispatch = createEventDispatcher()

  /** @deprecated Use `value` instead. */
  export let content = ''
  export let value = content
  export let fontFamilies = []
  export let disabled = null
  export let editorFontSize = '1em'
  export let language = 'en'

  /** @deprecated Use `fontSizeOptions.initialFontSize` instead. */
  export let defaultFontSize = 16
  /** @deprecated Use `fontSizeOptions.fontSizeUnit` instead. */
  export let sizeUnit = 'px'
  export let fontSizeOptions = {
    fontSizeUnit: sizeUnit,
    initialFontSize: defaultFontSize,
    fontSizeFactor: 1
  }

  const defaultClass = `${cssPrefix}wysiwyg`

  let element
  /** @type {Editor} */
  let editor
  let latestMarks = []
  let latestTextAlignment

  const translations = {
    en: {
      text: 'Text',
      font: 'Font',
      size: 'Size',
      color: 'Color',
      align: 'Align'
    },
    de: {
      text: 'Text',
      font: 'Schriftart',
      size: 'Größe',
      color: 'Farbe',
      align: 'Ausrichten'
    }
  }

  const textAlignmentOptions = [
    { icon: faAlignLeft, text: '', style: '', value: 'left' },
    { icon: faAlignCenter, text: '', style: '', value: 'center' },
    { icon: faAlignRight, text: '', style: '', value: 'right' },
    { icon: faAlignJustify, text: '', style: '', value: 'justify' }
  ]

  const baseExtensions = [
    Document,
    Color,
    FontSizeExtension.configure(fontSizeOptions),
    FontFamily,
    Text,
    Underline,
    Strike,
    Italic,
    CustomBold,
    Code,
    ParagraphMigration,
    // Highlight.configure({ multicolor: true }),
    TextAlign.configure({
      types: ['heading', 'paragraph']
    }),
    Heading
  ]

  $: if (disabled && editor) editor.setOptions({ editable: !disabled || !disabled.text })
  $: fontFamilyOptions = [{ text: 'Font family', value: '', style: '' }].concat(
    fontFamilies.map(family => ({ text: family, value: family, style: `font-family: ${family}` }))
  )
  $: selectedFontFamily =
    fontFamilyOptions.find(o => o.value === (editor && editor.getAttributes('textStyle').fontFamily)) ||
    fontFamilyOptions[0]

  onMount(() => {
    editor = new Editor({
      element,
      extensions: [
        ...baseExtensions,
        TextStyle.configure({
          HTMLAttributes: {
            style: `font-size: ${editorFontSize} !important`
          }
        })
      ],
      content: value,
      editable: !disabled || !disabled.text,
      editorProps: {
        transformPasted(slice, view) {
          let newSlice = new Slice(Fragment.empty, 1, 1)

          slice.content.forEach(node => {
            newSlice.content = newSlice.content.append(
              Fragment.from(editor.schema.node('paragraph', null, editor.schema.text(node.textContent, latestMarks)))
            )
          })

          return newSlice
        }
      },
      onCreate({ editor }) {
        latestTextAlignment = editor.getAttributes('paragraph').textAlign
        if (!editor.getAttributes('textStyle').fontSize) {
          let pFontSize = editor.getAttributes('paragraph').fontSize

          if (pFontSize) pFontSize *= fontSizeOptions.fontSizeFactor
          if (!pFontSize) pFontSize = fontSizeOptions.initialFontSize

          editor.chain().focus('all').setFontSize(pFontSize).run()
        }

        value = generateHTML(editor.getJSON(), [...baseExtensions, TextStyle])
      },
      onTransaction() {
        latestMarks = editor.state.selection.$from.marks()
        editor = editor
      },
      onUpdate: ({ editor }) => {
        //keep alignment when editor is empty
        if (editor.isEmpty && editor.getAttributes('paragraph').textAlign !== latestTextAlignment) {
          editor.chain().focus().setTextAlign(latestTextAlignment).run()
        }
        value = generateHTML(editor.getJSON(), [...baseExtensions, TextStyle])
        dispatch('change', { value })
      }
    })
  })

  onDestroy(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<div class="{defaultClass}-container">
  <div class="text-input">
    <p class="mb-2">{translations[language].text}</p>
    <div
      bind:this={element}
      class="mb-2 flex-grow-1 mr-2 {defaultClass}-text"
      class:disabled={disabled && disabled.text}
    />
  </div>

  {#if editor}
    {@const color = editor.getAttributes('textStyle').color || 'black'}

    <div class={defaultClass}>
      <div class="{defaultClass}-buttons">
        <div class="{defaultClass}-font">
          <p class="mb-2">{translations[language].font}</p>
          {#if fontFamilies.length}
            <WysiwygDropdown
              data={fontFamilyOptions}
              on:click={e => {
                editor.chain().focus().setFontFamily(e.detail.value).run()
              }}
              selectedEl={selectedFontFamily}
              disabled={editor.isActive('code') || (disabled && disabled.style)}
              id="font-family"
            />
          {/if}
          <button
            on:click={() => editor.chain().focus().toggleBold().run()}
            class:active={editor.isActive('bold')}
            disabled={!editor.can().chain().focus().toggleBold().run() || (disabled && disabled.style)}
          >
            <b>B</b>
          </button>
          <button
            on:click={() => editor.chain().focus().toggleItalic().run()}
            class:active={editor.isActive('italic')}
            disabled={!editor.can().chain().focus().toggleItalic().run() || (disabled && disabled.style)}
          >
            <i>I</i>
          </button>
          <button
            on:click={() => editor.chain().focus().toggleUnderline().run()}
            class:active={editor.isActive('underline')}
            disabled={!editor.can().chain().focus().toggleUnderline().run() || (disabled && disabled.style)}
          >
            <u>U</u>
          </button>
        </div>

        <div class="{defaultClass}-size">
          <p class="mb-2">{translations[language].size}</p>
          <div class={`${defaultClass}-input-container`} id="font-size">
            <input
              type="number"
              min="1"
              value={editor.getAttributes('textStyle').fontSize}
              on:input={e => editor.chain().setFontSize(e.target.value).run()}
              disabled={!editor.can().chain().setFontSize().run() || (disabled && disabled.style)}
            />
          </div>
        </div>

        <div class="{defaultClass}-color">
          <ColorPicker
            on:change={event => editor.commands.setColor(event.detail.value)}
            value={rgbStringToHex(color) || color}
            disabled={!editor.can().chain().focus().setColor().run() || (disabled && disabled.style)}
            label={translations[language].color}
          />

          <!-- <FillEditor
            {defaultClass}
            selectedFillColor={editor.getAttributes('highlight').color}
            on:fill={e => {
              editor.chain().focus().setHighlight({ color: e.detail.color }).run()
            }}
            on:reset={() => {
              editor.commands.unsetHighlight()
            }}
          /> -->
        </div>

        <div class="{defaultClass}-align">
          <p class="mb-2">{translations[language].align}</p>
          <WysiwygDropdown
            data={textAlignmentOptions}
            selectedEl={textAlignmentOptions.find(el => el.value === editor.getAttributes('paragraph').textAlign) ??
              textAlignmentOptions[0]}
            on:click={e => {
              editor.chain().focus().setTextAlign(e.detail.value).run()
              latestTextAlignment = e.detail.value
            }}
            disabled={disabled && disabled.style}
            id="text-alignment"
          />
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  :global(.ProseMirror) {
    height: 100%;
    width: 100%;
  }

  .disabled {
    background-color: #fafafa;
  }
</style>
